// One plotted series. Polymorphic over `:type`; axis ids come from the
// orientation model so a series always binds the shared category axis +
// its value-axis side, in either orientation.

import { type ComponentType, useId } from 'react';
import { Area, type AreaProps, Bar, Scatter } from 'recharts';

import type { SeriesSpec } from '#core/lib/expr/ops/dashboard/value.ts';
import {
  CHART_SHADE,
  fillOpacity,
  type MantineColor,
  mantineColorVar,
  shiftShade,
} from '#core/lib/formatters/colors.ts';

import { dashArray, isSolidStroke } from '../cell.ts';
import { scatterSizeKey } from './band-keys.ts';
import type { ChartOrientation } from './chartOrientation.ts';

// recharts series component per `:type`. A `scatter` plots discrete
// points (optionally bubble-sized via a `<ZAxis>` wired by the chart).
const SERIES_COMPONENT = { line: GradientArea, area: Area, bar: Bar, scatter: Scatter } as const;
// Dashboard `Curve` → recharts line/area interpolation `type`.
const CURVE_RECHARTS = { linear: 'linear', smooth: 'monotone', step: 'step' } as const;

export function ChartSeries<
  PropsType extends React.ComponentProps<(typeof SERIES_COMPONENT)[keyof typeof SERIES_COMPONENT]>,
>({
  o,
  series: s,
  color,
  shadeOffset,
  seriesCount,
  bandCount,
  stacked,
  ...props
}: {
  o: ChartOrientation;
  series: SeriesSpec;
  color: MantineColor;
  shadeOffset: number;
  seriesCount: number;
  bandCount: number;
  stacked: boolean;
} & Partial<PropsType>) {
  // A lone line series reads better as a filled area — but only when no
  // bands are present (a band + filled area overlap and look muddy, so
  // keep the series a crisp line in that case). Scatter/bar are never
  // promoted.
  const renderType = seriesCount === 1 && bandCount === 0 && s.type === 'line' ? 'area' : s.type;
  const SeriesComponent = SERIES_COMPONENT[renderType] as ComponentType<Record<string, unknown>>;
  const fillVar = mantineColorVar(color, shiftShade(CHART_SHADE.seriesFill, shadeOffset));
  const strokeVar = mantineColorVar(color, shiftShade(CHART_SHADE.seriesStroke, shadeOffset));
  const markerVar = mantineColorVar(color, shiftShade(CHART_SHADE.marker, shadeOffset));
  const valueSide = s.axis ?? 'left';
  const isScatter = renderType === 'scatter';
  // Lines never stack; bars/areas share a stackId per value axis.
  const stackId = stacked && (renderType === 'bar' || renderType === 'area') ? `stack-${valueSide}` : undefined;
  // Density-aware bar width cap. Recharts spaces bars by slot; without a
  // cap a single-series chart paints absurdly wide bars, but a hard 40
  // crushes multi-series clusters into slivers. Shrink the cap as the
  // cluster gets denser (more side-by-side bars per x slot), clamped to
  // a usable range.
  const maxBarSize = Math.min(60, Math.max(20, Math.round(120 / Math.max(1, seriesCount))));
  const dot = isSolidStroke(s.stroke) ? { r: 2, fill: markerVar, fillOpacity: 1, strokeWidth: 1 } : false;

  // A scatter is point-only: no connecting stroke, curve, stack, or bar
  // sizing. It binds the same shared category + value axes as every
  // other series, plus the optional z-axis (wired by the chart) so a
  // `:z` channel sizes each point. Its color rides the marker fill.
  if (isScatter) {
    return (
      <Scatter
        dataKey={s.id}
        fill={markerVar}
        fillOpacity={fillOpacity.strong}
        isAnimationActive={false}
        legendType="circle"
        name={s.label}
        stroke={strokeVar}
        strokeWidth={1}
        {...(s.z === undefined ? {} : { zAxisId: scatterSizeKey(s.id) })}
        {...o.seriesAxisIds(valueSide)}
        {...(props as object)}
      />
    );
  }

  return (
    <SeriesComponent
      activeDot={{
        r: 4,
        fill: markerVar,
        fillOpacity: 1,
        strokeWidth: 1.5,
        stroke: strokeVar,
      }}
      connectNulls={false}
      dataKey={s.id}
      dot={dot}
      fill={fillVar}
      fillOpacity={renderType === 'bar' ? fillOpacity.strong : fillOpacity.light}
      isAnimationActive={false}
      legendType="square"
      maxBarSize={maxBarSize}
      name={s.label}
      stroke={strokeVar}
      strokeDasharray={dashArray(s.stroke)}
      strokeWidth={1.5}
      type={CURVE_RECHARTS[s.curve]}
      {...(stackId === undefined ? {} : { stackId })}
      {...o.seriesAxisIds(valueSide)}
      {...(props as object)}
    />
  );
}

function GradientArea<DataPointType, ValueAxisType>({
  fill,
  ...props
}: AreaProps<DataPointType, ValueAxisType> & { fill: string }) {
  const id = useId();

  return (
    <>
      <Area {...props} fill={`url(#${id})`} fillOpacity={0.1} />
      <defs>
        <linearGradient id={id} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor={fill} stopOpacity={1} />
          <stop offset="30%" stopColor={fill} stopOpacity={0} />
        </linearGradient>
      </defs>
    </>
  );
}
